import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { Card, CardContent, CardHeader } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import {
    Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '../../components/ui/dialog';
import {
    DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from '../../components/ui/dropdown-menu';
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import { Checkbox } from '../../components/ui/checkbox';
import { toast } from 'sonner';
import { FileText, Download, User, Package, Loader2, Search, Mail, MapPin, CheckCircle, XCircle, Clock, Send, ChevronDown, Eye, Trash2 } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const statusConfig = {
    sent: { label: 'Sent', color: 'bg-blue-100 text-blue-700', icon: Send },
    accepted: { label: 'Accepted', color: 'bg-green-100 text-green-700', icon: CheckCircle },
    declined: { label: 'Declined', color: 'bg-red-100 text-red-700', icon: XCircle },
    expired: { label: 'Expired', color: 'bg-gray-100 text-gray-500', icon: Clock }
};

const ClickableStatCard = ({ title, count, value, icon: Icon, color, active, onClick }) => (
    <div
        className={`p-3 sm:p-4 bg-white border cursor-pointer transition-all min-w-0 ${active ? 'border-brand-lime ring-2 ring-inset ring-brand-lime/30' : 'border-brand-gray-200 hover:border-brand-lime/50'}`}
        onClick={onClick}
        data-testid={`stat-card-${title.toLowerCase()}`}
    >
        <div className="flex items-center justify-between">
            <p className="text-xs font-medium text-brand-gray-500 uppercase tracking-wide">{title}</p>
            <div className={`hidden sm:flex h-8 w-8 sm:h-10 sm:w-10 items-center justify-center flex-shrink-0 ${color}`}>
                <Icon className="h-4 w-4 sm:h-5 sm:w-5" />
            </div>
        </div>
        <p className="text-xl sm:text-2xl font-bold font-heading text-brand-charcoal mt-1">{count}</p>
        <p className="text-xs sm:text-sm text-brand-gray-500">£{value.toLocaleString('en-GB', { minimumFractionDigits: 0 })}</p>
    </div>
);

const QuoteDetailDialog = ({ quote, open, onOpenChange, onStatusChange, token }) => {
    const [downloading, setDownloading] = useState(false);
    const [updatingStatus, setUpdatingStatus] = useState(false);
    if (!quote) return null;

    const handleDownloadPdf = async () => {
        setDownloading(true);
        try {
            const response = await axios.get(`${API}/quotes/${quote.id}/pdf`, { headers: { Authorization: `Bearer ${token}` }, responseType: 'blob' });
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `${quote.reference_number}.pdf`);
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
        } catch { toast.error('Failed to download PDF'); }
        finally { setDownloading(false); }
    };

    const handleStatusChange = async (newStatus) => {
        setUpdatingStatus(true);
        try {
            await axios.put(`${API}/admin/quotes/${quote.id}/status`, { status: newStatus }, { headers: { Authorization: `Bearer ${token}` } });
            toast.success(`Quote marked as ${newStatus}`);
            onStatusChange();
        } catch { toast.error('Failed to update status'); }
        finally { setUpdatingStatus(false); }
    };

    const StatusBadge = statusConfig[quote.status] || statusConfig.sent;
    const vatAmount = quote.vat_amount || 0;
    const totalIncVat = quote.total_inc_vat || quote.total_price;

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="sm:max-w-[600px]">
                <DialogHeader>
                    <DialogTitle className="font-heading text-xl flex items-center gap-3">
                        Quote {quote.reference_number}
                        <span className={`inline-flex items-center gap-1 px-2 py-1 text-xs font-medium ${StatusBadge.color}`}>
                            <StatusBadge.icon className="h-3 w-3" /> {StatusBadge.label}
                        </span>
                    </DialogTitle>
                    <DialogDescription>Created on {new Date(quote.created_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })}</DialogDescription>
                </DialogHeader>
                <div className="space-y-6">
                    <div className="space-y-3">
                        <h4 className="text-sm font-bold uppercase tracking-wide text-brand-gray-500">Customer Details</h4>
                        <div className="p-4 bg-brand-gray-50 space-y-2">
                            <div className="flex items-center gap-2"><User className="h-4 w-4 text-brand-gray-400" /><span className="font-medium">{quote.customer.company_name || quote.customer.name}</span></div>
                            {quote.customer.contact_name && <div className="flex items-center gap-2 text-sm text-brand-gray-600 pl-6">{quote.customer.contact_name}{quote.customer.contact_job_title && ` (${quote.customer.contact_job_title})`}</div>}
                            <div className="flex items-center gap-2"><Mail className="h-4 w-4 text-brand-gray-400" /><span className="text-sm text-brand-gray-600">{quote.customer.email}</span></div>
                            <div className="flex items-start gap-2"><MapPin className="h-4 w-4 text-brand-gray-400 mt-0.5" /><span className="text-sm text-brand-gray-600">{quote.customer.address}</span></div>
                        </div>
                    </div>
                    <div className="space-y-3">
                        <h4 className="text-sm font-bold uppercase tracking-wide text-brand-gray-500">Quote Details</h4>
                        <div className="p-4 bg-brand-gray-50 space-y-3">
                            {(quote.items && quote.items.length > 0) ? (
                                <div className="space-y-2">
                                    {quote.items.map((item) => (
                                        <div key={item.pack_id} className="flex items-center justify-between gap-2 py-1">
                                            <div className="flex-1 min-w-0">
                                                <div className="flex items-center gap-2">
                                                    <Package className="h-4 w-4 text-brand-gray-400 flex-shrink-0" />
                                                    <span className="font-medium text-sm">{item.pack_name}</span>
                                                </div>
                                                <span className="text-xs text-brand-gray-500 pl-6">
                                                    Qty: {item.quantity} x £{item.unit_price?.toLocaleString('en-GB', { minimumFractionDigits: 2 })}
                                                    {item.vat_rate > 0 && ` (${item.vat_rate_name} ${item.vat_rate}%)`}
                                                </span>
                                            </div>
                                            <span className="text-sm font-bold whitespace-nowrap">£{item.line_total_ex_vat?.toLocaleString('en-GB', { minimumFractionDigits: 2 })}</span>
                                        </div>
                                    ))}
                                </div>
                            ) : (
                                <>
                                    <div className="flex items-center gap-2"><Package className="h-4 w-4 text-brand-gray-400" /><span className="font-medium">{quote.spare_pack.name}</span></div>
                                    <p className="text-sm text-brand-gray-600 pl-6">{quote.spare_pack.description}</p>
                                </>
                            )}
                            <div className="pt-2 border-t border-brand-gray-200 space-y-1">
                                <div className="flex justify-between items-center text-sm">
                                    <span className="text-brand-gray-500">Subtotal (Ex VAT)</span>
                                    <span className="font-medium">£{(quote.total_ex_vat || quote.total_price).toLocaleString('en-GB', { minimumFractionDigits: 2 })}</span>
                                </div>
                                <div className="flex justify-between items-center text-sm">
                                    <span className="text-brand-gray-500">VAT</span>
                                    <span className="font-medium">£{(quote.total_vat || vatAmount).toLocaleString('en-GB', { minimumFractionDigits: 2 })}</span>
                                </div>
                                <div className="flex justify-between items-center pt-1 border-t border-brand-gray-200">
                                    <span className="text-brand-gray-500 font-bold">Total (Inc VAT)</span>
                                    <span className="text-xl font-bold font-heading text-brand-charcoal">£{(quote.total_inc_vat || totalIncVat).toLocaleString('en-GB', { minimumFractionDigits: 2 })}</span>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="space-y-3">
                        <h4 className="text-sm font-bold uppercase tracking-wide text-brand-gray-500">Prepared By</h4>
                        <div className="flex items-center gap-3 p-4 bg-brand-gray-50">
                            <div className="h-10 w-10 bg-brand-lime flex items-center justify-center"><User className="h-5 w-5 text-black" /></div>
                            <div><p className="font-medium">{quote.engineer_name}</p><p className="text-sm text-brand-gray-500">{quote.engineer_email}</p></div>
                        </div>
                    </div>
                    <div className="flex gap-3 pt-4 border-t border-brand-gray-200">
                        <Button variant="outline" onClick={handleDownloadPdf} disabled={downloading} className="flex-1" data-testid="download-quote-pdf">
                            {downloading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Download className="h-4 w-4 mr-2" />} Download PDF
                        </Button>
                        <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                                <Button className="flex-1 bg-[#7AC943] hover:bg-[#68B035] text-black" disabled={updatingStatus} data-testid="change-status-dropdown">
                                    {updatingStatus && <Loader2 className="h-4 w-4 animate-spin mr-2" />} Change Status <ChevronDown className="h-4 w-4 ml-2" />
                                </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                                <DropdownMenuItem onClick={() => handleStatusChange('accepted')} data-testid="status-accepted"><CheckCircle className="h-4 w-4 mr-2 text-green-600" /> Mark as Accepted</DropdownMenuItem>
                                <DropdownMenuItem onClick={() => handleStatusChange('declined')} data-testid="status-declined"><XCircle className="h-4 w-4 mr-2 text-red-600" /> Mark as Declined</DropdownMenuItem>
                                <DropdownMenuItem onClick={() => handleStatusChange('expired')} data-testid="status-expired"><Clock className="h-4 w-4 mr-2 text-gray-500" /> Mark as Expired</DropdownMenuItem>
                                <DropdownMenuItem onClick={() => handleStatusChange('sent')} data-testid="status-sent"><Send className="h-4 w-4 mr-2 text-blue-600" /> Mark as Sent</DropdownMenuItem>
                            </DropdownMenuContent>
                        </DropdownMenu>
                    </div>
                </div>
            </DialogContent>
        </Dialog>
    );
};

const QuotesManagement = () => {
    const { token, isGlobalAdmin } = useAuth();
    const [quotes, setQuotes] = useState([]);
    const [stats, setStats] = useState(null);
    const [engineers, setEngineers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchQuery, setSearchQuery] = useState('');
    const [statusFilter, setStatusFilter] = useState('sent');
    const [engineerFilter, setEngineerFilter] = useState('all');
    const [selectedQuote, setSelectedQuote] = useState(null);
    const [detailDialogOpen, setDetailDialogOpen] = useState(false);
    const [selectedIds, setSelectedIds] = useState(new Set());
    const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
    const [deleting, setDeleting] = useState(false);

    const canDelete = isGlobalAdmin();

    useEffect(() => { fetchData(); }, []);

    const fetchData = async () => {
        try {
            const [quotesRes, statsRes, engineersRes] = await Promise.all([
                axios.get(`${API}/quotes`, { headers: { Authorization: `Bearer ${token}` } }),
                axios.get(`${API}/admin/quotes/stats`, { headers: { Authorization: `Bearer ${token}` } }),
                axios.get(`${API}/admin/quotes/engineers`, { headers: { Authorization: `Bearer ${token}` } })
            ]);
            setQuotes(quotesRes.data);
            setStats(statsRes.data);
            setEngineers(engineersRes.data);
        } catch { toast.error('Failed to load quotes'); }
        finally { setLoading(false); }
    };

    const filteredQuotes = quotes.filter(quote => {
        const matchesSearch =
            quote.reference_number.toLowerCase().includes(searchQuery.toLowerCase()) ||
            (quote.customer.company_name || quote.customer.name || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
            quote.customer.email.toLowerCase().includes(searchQuery.toLowerCase()) ||
            quote.engineer_name.toLowerCase().includes(searchQuery.toLowerCase());
        const matchesStatus = statusFilter === 'all' || quote.status === statusFilter;
        const matchesEngineer = engineerFilter === 'all' || quote.engineer_id === engineerFilter;
        return matchesSearch && matchesStatus && matchesEngineer;
    });

    const toggleSelect = (id) => {
        setSelectedIds(prev => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id); else next.add(id);
            return next;
        });
    };

    const toggleSelectAll = () => {
        if (selectedIds.size === filteredQuotes.length) {
            setSelectedIds(new Set());
        } else {
            setSelectedIds(new Set(filteredQuotes.map(q => q.id)));
        }
    };

    const handleBulkDelete = async () => {
        if (selectedIds.size === 0) return;
        setDeleting(true);
        try {
            await axios.post(`${API}/admin/quotes/bulk-delete`, { ids: Array.from(selectedIds) }, { headers: { Authorization: `Bearer ${token}` } });
            toast.success(`${selectedIds.size} quote(s) deleted`);
            setSelectedIds(new Set());
            setDeleteConfirmOpen(false);
            fetchData();
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Failed to delete quotes');
        } finally { setDeleting(false); }
    };

    if (loading) {
        return <div className="flex items-center justify-center min-h-[60vh]"><Loader2 className="h-8 w-8 animate-spin text-brand-lime" /></div>;
    }

    const formatCurrency = (val) => `£${(val || 0).toLocaleString('en-GB', { minimumFractionDigits: 2 })}`;
    const allSelected = filteredQuotes.length > 0 && selectedIds.size === filteredQuotes.length;

    return (
        <div className="animate-fade-in space-y-6">
            <div className="flex items-center justify-between gap-4">
                <div>
                    <h1 className="text-3xl font-bold font-heading text-brand-charcoal">Quotes</h1>
                    <p className="text-brand-gray-500 mt-1">View and manage all quotes</p>
                </div>
                {canDelete && selectedIds.size > 0 && (
                    <Button
                        variant="destructive"
                        onClick={() => setDeleteConfirmOpen(true)}
                        data-testid="bulk-delete-btn"
                    >
                        <Trash2 className="h-4 w-4 mr-2" />
                        Delete {selectedIds.size} Quote{selectedIds.size > 1 ? 's' : ''}
                    </Button>
                )}
            </div>

            {/* Clickable Stats */}
            {stats && (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-2 sm:gap-4">
                    <ClickableStatCard title="Sent" count={stats.sent.count} value={stats.sent.value} icon={Send} color="bg-blue-100 text-blue-600" active={statusFilter === 'sent'} onClick={() => setStatusFilter(statusFilter === 'sent' ? 'all' : 'sent')} />
                    <ClickableStatCard title="Accepted" count={stats.accepted.count} value={stats.accepted.value} icon={CheckCircle} color="bg-green-100 text-green-600" active={statusFilter === 'accepted'} onClick={() => setStatusFilter(statusFilter === 'accepted' ? 'all' : 'accepted')} />
                    <ClickableStatCard title="Declined" count={stats.declined.count} value={stats.declined.value} icon={XCircle} color="bg-red-100 text-red-600" active={statusFilter === 'declined'} onClick={() => setStatusFilter(statusFilter === 'declined' ? 'all' : 'declined')} />
                    <ClickableStatCard title="Expired" count={stats.expired.count} value={stats.expired.value} icon={Clock} color="bg-gray-100 text-gray-500" active={statusFilter === 'expired'} onClick={() => setStatusFilter(statusFilter === 'expired' ? 'all' : 'expired')} />
                </div>
            )}

            <Card className="border-0 shadow-lg">
                <CardHeader className="pb-4">
                    <div className="flex flex-col sm:flex-row gap-3">
                        <div className="relative flex-1">
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-brand-gray-400" />
                            <Input type="text" placeholder="Search quotes..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} className="h-11 pl-10" data-testid="quotes-search" />
                        </div>
                        <div className="flex gap-2">
                            <Select value={statusFilter} onValueChange={setStatusFilter}>
                                <SelectTrigger className="h-11 w-full sm:w-[150px]" data-testid="status-filter-select">
                                    <SelectValue placeholder="Status" />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="all">All Status</SelectItem>
                                    <SelectItem value="sent">Sent</SelectItem>
                                    <SelectItem value="accepted">Accepted</SelectItem>
                                    <SelectItem value="declined">Declined</SelectItem>
                                    <SelectItem value="expired">Expired</SelectItem>
                                </SelectContent>
                            </Select>
                            {engineers.length > 0 && (
                                <Select value={engineerFilter} onValueChange={setEngineerFilter}>
                                    <SelectTrigger className="h-11 w-full sm:w-[180px]" data-testid="engineer-filter-select">
                                        <SelectValue placeholder="Engineer" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="all">All Engineers</SelectItem>
                                        {engineers.map(eng => (
                                            <SelectItem key={eng.engineer_id} value={eng.engineer_id}>{eng.engineer_name}</SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            )}
                        </div>
                    </div>
                </CardHeader>
                <CardContent className="px-0 pb-0">
                    {filteredQuotes.length === 0 ? (
                        <div className="text-center py-12 px-6">
                            <FileText className="h-12 w-12 mx-auto text-brand-gray-300 mb-3" />
                            <p className="text-brand-gray-500">No quotes found</p>
                        </div>
                    ) : (
                        <>
                            {/* Mobile card list */}
                            <div className="md:hidden divide-y divide-brand-gray-200">
                                {filteredQuotes.map(quote => {
                                    const StatusBadge = statusConfig[quote.status] || statusConfig.sent;
                                    return (
                                        <div key={quote.id} className="px-4 py-3 flex items-start gap-3" data-testid={`quote-row-${quote.id}`}>
                                            {canDelete && (
                                                <div className="pt-1" onClick={(e) => e.stopPropagation()}>
                                                    <Checkbox
                                                        checked={selectedIds.has(quote.id)}
                                                        onCheckedChange={() => toggleSelect(quote.id)}
                                                        data-testid={`quote-checkbox-mobile-${quote.id}`}
                                                    />
                                                </div>
                                            )}
                                            <div className="flex-1 cursor-pointer active:bg-brand-gray-50" onClick={() => { setSelectedQuote(quote); setDetailDialogOpen(true); }}>
                                                <div className="flex items-start justify-between gap-2 mb-1">
                                                    <span className="text-sm font-medium text-brand-lime">{quote.reference_number}</span>
                                                    <span className={`inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded-sm flex-shrink-0 ${StatusBadge.color}`}>
                                                        <StatusBadge.icon className="h-3 w-3" /> {StatusBadge.label}
                                                    </span>
                                                </div>
                                                <p className="text-sm font-medium text-brand-charcoal">{quote.customer.company_name || quote.customer.name}</p>
                                                <div className="flex items-center justify-between mt-1">
                                                    <span className="text-xs text-brand-gray-500">{quote.engineer_name} · {new Date(quote.created_at).toLocaleDateString('en-GB', { day: '2-digit', month: 'short' })}</span>
                                                    <span className="text-sm font-bold">{formatCurrency(quote.total_inc_vat || quote.total_price)}</span>
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>

                            {/* Desktop table */}
                            <div className="hidden md:block overflow-x-auto">
                                <table className="admin-table">
                                    <colgroup>
                                        {canDelete && <col style={{ width: '3%' }} />}
                                        <col style={{ width: canDelete ? '10%' : '11%' }} />
                                        <col style={{ width: '7%' }} />
                                        <col style={{ width: canDelete ? '17%' : '18%' }} />
                                        <col style={{ width: '12%' }} />
                                        <col className="hidden lg:table-column" style={{ width: '16%' }} />
                                        <col style={{ width: '12%' }} />
                                        <col className="hidden lg:table-column" style={{ width: '12%' }} />
                                        <col style={{ width: '9%' }} />
                                        <col style={{ width: '5%' }} />
                                    </colgroup>
                                    <thead>
                                        <tr>
                                            {canDelete && (
                                                <th className="text-center" onClick={(e) => e.stopPropagation()}>
                                                    <Checkbox
                                                        checked={allSelected}
                                                        onCheckedChange={toggleSelectAll}
                                                        data-testid="select-all-checkbox"
                                                    />
                                                </th>
                                            )}
                                            <th>Reference</th>
                                            <th>Date</th>
                                            <th>Customer</th>
                                            <th>Engineer</th>
                                            <th className="hidden lg:table-cell">Pack</th>
                                            <th className="text-right">Ex VAT</th>
                                            <th className="text-right hidden lg:table-cell">Inc VAT</th>
                                            <th className="text-center">Status</th>
                                            <th className="text-center"></th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {filteredQuotes.map(quote => {
                                            const StatusBadge = statusConfig[quote.status] || statusConfig.sent;
                                            return (
                                                <tr key={quote.id} className={`cursor-pointer hover:bg-brand-gray-50 ${selectedIds.has(quote.id) ? 'bg-red-50/50' : ''}`} onClick={() => { setSelectedQuote(quote); setDetailDialogOpen(true); }} data-testid={`quote-row-desktop-${quote.id}`}>
                                                    {canDelete && (
                                                        <td className="text-center" onClick={(e) => e.stopPropagation()}>
                                                            <Checkbox
                                                                checked={selectedIds.has(quote.id)}
                                                                onCheckedChange={() => toggleSelect(quote.id)}
                                                                data-testid={`quote-checkbox-${quote.id}`}
                                                            />
                                                        </td>
                                                    )}
                                                    <td>
                                                        <span className="font-medium text-brand-lime whitespace-nowrap">{quote.reference_number}</span>
                                                    </td>
                                                    <td className="text-brand-gray-500 whitespace-nowrap">
                                                        {new Date(quote.created_at).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: '2-digit' })}
                                                    </td>
                                                    <td>
                                                        <span className="font-medium block">{quote.customer.company_name || quote.customer.name}</span>
                                                        {quote.customer.contact_name && (
                                                            <span className="text-xs text-brand-gray-400 block">{quote.customer.contact_name}</span>
                                                        )}
                                                    </td>
                                                    <td className="text-brand-gray-600">{quote.engineer_name}</td>
                                                    <td className="hidden lg:table-cell text-brand-gray-600">
                                                        {quote.items?.length > 1
                                                            ? `${quote.items.length} packs`
                                                            : quote.items?.[0]?.pack_name || quote.spare_pack?.name}
                                                    </td>
                                                    <td className="text-right font-semibold whitespace-nowrap">{formatCurrency(quote.total_price)}</td>
                                                    <td className="text-right hidden lg:table-cell font-semibold whitespace-nowrap">{formatCurrency(quote.total_inc_vat || quote.total_price)}</td>
                                                    <td className="text-center">
                                                        <span className={`inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium rounded-sm ${StatusBadge.color}`}>
                                                            <StatusBadge.icon className="h-3 w-3" /> {StatusBadge.label}
                                                        </span>
                                                    </td>
                                                    <td className="text-center" onClick={(e) => e.stopPropagation()}>
                                                        <Button size="sm" variant="ghost" onClick={() => { setSelectedQuote(quote); setDetailDialogOpen(true); }} className="h-8 w-8 p-0" data-testid={`view-quote-${quote.id}`}>
                                                            <Eye className="h-4 w-4" />
                                                        </Button>
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        </>
                    )}
                </CardContent>
            </Card>

            <QuoteDetailDialog quote={selectedQuote} open={detailDialogOpen} onOpenChange={setDetailDialogOpen} onStatusChange={fetchData} token={token} />

            {/* Bulk Delete Confirmation */}
            <Dialog open={deleteConfirmOpen} onOpenChange={setDeleteConfirmOpen}>
                <DialogContent className="sm:max-w-[450px]">
                    <DialogHeader>
                        <DialogTitle className="text-red-600 font-heading text-xl">Delete Quotes</DialogTitle>
                        <DialogDescription>
                            Are you sure you want to permanently delete <strong>{selectedIds.size}</strong> quote{selectedIds.size > 1 ? 's' : ''}? This action cannot be undone.
                        </DialogDescription>
                    </DialogHeader>
                    <DialogFooter className="gap-2">
                        <Button variant="outline" onClick={() => setDeleteConfirmOpen(false)}>Cancel</Button>
                        <Button variant="destructive" onClick={handleBulkDelete} disabled={deleting} data-testid="confirm-bulk-delete-btn">
                            {deleting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Trash2 className="h-4 w-4 mr-2" />}
                            Delete {selectedIds.size} Quote{selectedIds.size > 1 ? 's' : ''}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
};

export default QuotesManagement;
